'use client';
import React, {useCallback, useEffect, useState} from "react";
import Link from "next/link";
import "./keyboard.css";
import {FaCheck, FaChevronDown, FaChevronLeft, FaChevronRight, FaChevronUp, FaExclamationTriangle, FaInfoCircle, FaQuestionCircle, FaRedo, FaTimes, FaWindows, FaKeyboard} from "react-icons/fa";
import {OverlayTrigger, Tooltip} from "react-bootstrap";
import {CgMenuBoxed} from "react-icons/cg";
import {useKeyboardState} from "./KeyboardStateContext";
import KeyboardType, {hasKeyName, mapSwitchableBottomRows} from "./components/KeyboardType/KeyboardType";
import keyboardSilhouetteLayouts from "./keyboardSilhouetteLayouts";
import keyboardCustomLayouts from "./keyboardCustomLayouts";

//##################################
// Keyboard array row may contain
//##################################
// name[string] - required,
// keys[array] - required at least one item in array,
// excluded[boolean] - optional,
// class[string] - optional
// isPressed[boolean]
// wasPressed[boolean]

// CLEANED UP FOR GRID: Removed "merge" duplicates. 
// Grid handles vertical spanning (row-span) and horizontal spanning (col-span) natively.

const fullSizeKeys = [
    {
        section: [
            {
                row: [
                    {name: "Escape", keys: ["Esc"]},
                    {name: "F1", keys: ["F1"]},
                    {name: "F2", keys: ["F2"]},
                    {name: "F3", keys: ["F3"]},
                    {name: "F4", keys: ["F4"]},
                    {name: "F5", keys: ["F5"]},
                    {name: "F6", keys: ["F6"]},
                    {name: "F7", keys: ["F7"]},
                    {name: "F8", keys: ["F8"]},
                    {name: "F9", keys: ["F9"]},
                    {name: "F10", keys: ["F10"]},
                    {name: "F11", keys: ["F11"]},
                    {name: "F12", keys: ["F12"]},
                ]
            },
            {
                row: [
                    {name: "Backquote", keys: ["`", "~"]},
                    {name: "Digit1", keys: ["1", "!"]},
                    {name: "Digit2", keys: ["2", "@"]},
                    {name: "Digit3", keys: ["3", "#"]},
                    {name: "Digit4", keys: ["4", "$"]},
                    {name: "Digit5", keys: ["5", "%"]},
                    {name: "Digit6", keys: ["6", "^"]},
                    {name: "Digit7", keys: ["7", "&"]},
                    {name: "Digit8", keys: ["8", "*"]},
                    {name: "Digit9", keys: ["9", "("]},
                    {name: "Digit0", keys: ["0", ")"]},
                    {name: "Minus", keys: ["-", "_"]},
                    {name: "Equal", keys: ["=", "+"]},
                    {name: "Backspace", keys: ["Backspace"]},
                ]
            },
            {
                row: [
                    {name: "Tab", keys: ["Tab"]},
                    {name: "KeyQ", keys: ["q"]},
                    {name: "KeyW", keys: ["w"]},
                    {name: "KeyE", keys: ["e"]},
                    {name: "KeyR", keys: ["r"]},
                    {name: "KeyT", keys: ["t"]},
                    {name: "KeyY", keys: ["y"]},
                    {name: "KeyU", keys: ["u"]},
                    {name: "KeyI", keys: ["i"]},
                    {name: "KeyO", keys: ["o"]},
                    {name: "KeyP", keys: ["p"]},
                    {name: "BracketLeft", keys: ["[", "{"]},
                    {name: "BracketRight", keys: ["]", "}"]},
                    {name: "Backslash", keys: ["\\", "|"]},
                ]
            },
            {
                row: [
                    {name: "CapsLock", keys: ["Caps"]},
                    {name: "KeyA", keys: ["a"]},
                    {name: "KeyS", keys: ["s"]},
                    {name: "KeyD", keys: ["d"]},
                    {name: "KeyF", keys: ["f"]},
                    {name: "KeyG", keys: ["g"]},
                    {name: "KeyH", keys: ["h"]},
                    {name: "KeyJ", keys: ["j"]},
                    {name: "KeyK", keys: ["k"]},
                    {name: "KeyL", keys: ["l"]},
                    {name: "Semicolon", keys: [";", ":"]},
                    {name: "Quote", keys: ["'", "\""]},
                    {name: "Enter", keys: ["Enter"]}
                ]
            },
            {
                row: [
                    {name: "ShiftLeft", keys: ["Shift"]},
                    {name: "KeyZ", keys: ["z"]},
                    {name: "KeyX", keys: ["x"]},
                    {name: "KeyC", keys: ["c"]},
                    {name: "KeyV", keys: ["v"]},
                    {name: "KeyB", keys: ["b"]},
                    {name: "KeyN", keys: ["n"]},
                    {name: "KeyM", keys: ["m"]},
                    {name: "Comma", keys: [",", "<"]},
                    {name: "Period", keys: [".", ">"]},
                    {name: "Slash", keys: ["/", "?"]},
                    {name: "ShiftRight", keys: ["Shift"]}
                ]
            },
            {
                row: [
                    {name: "ControlLeft", keys: ["Ctrl"]},
                    {
                        name: "MetaLeft", keys: [
                            <FaWindows />], special: true, excluded: true
                    },
                    {name: "AltLeft", keys: ["Alt"]},
                    {name: "Space", keys: ["Space"]},
                    {
                        name: "MetaRight", keys: [
                            <FaWindows />], special: true, excluded: true
                    },
                    {name: "AltRight", keys: ["Alt"]},
                    {
                        name: "ContextMenu", keys: [
                            <CgMenuBoxed />], special: true
                    },
                    {name: "ControlRight", keys: ["Ctrl"]},
                ]
            },
        ]
    },
    {
        section: [
            {
                row: [
                    {name: "PrintScreen", keys: ["Prt Sc", "Sys Rq"]},
                    {name: "ScrollLock", keys: ["Scr Lk"]},
                    {name: "Pause", keys: ["Pause"]},
                ],
            },
            {
                row: [
                    {name: "Insert", keys: ["Ins"]},
                    {name: "Home", keys: ["Home"]},
                    {name: "PageUp", keys: ["Page\nUp"]},
                ]
            },
            {
                row: [
                    {name: "Delete", keys: ["Del"]},
                    {name: "End", keys: ["End"]},
                    {name: "PageDown", keys: ["Page\nDown"]},
                ]
            },
            {
                row: [
                    {name: "SPACER", keys: ["spc"], excluded: true},
                    {name: "SPACER", keys: ["spc"], excluded: true},
                    {name: "SPACER", keys: ["spc"], excluded: true},
                ]
            },
            {
                row: [
                    {name: "SPACER", keys: ["spc"], excluded: true},
                    {
                        name: "ArrowUp", keys: [
                            <FaChevronUp />]
                    },
                    {name: "SPACER", keys: ["spc"], excluded: true},
                ]
            },
            {
                row: [
                    {
                        name: "ArrowLeft", keys: [
                            <FaChevronLeft />]
                    },
                    {
                        name: "ArrowDown", keys: [
                            <FaChevronDown />]
                    },
                    {
                        name: "ArrowRight", keys: [
                            <FaChevronRight />]
                    },
                ]
            },
        ]
    },
    {
        section: [
            {
                row: [
                    {name: "SPACER", keys: ["spc"], excluded: true},
                    {name: "SPACER", keys: ["spc"], excluded: true},
                    {name: "SPACER", keys: ["spc"], excluded: true},
                    {name: "SPACER", keys: ["spc"], excluded: true},
                ]
            },
            {
                row: [
                    {name: "NumLock", keys: ["Num Lk"]},
                    {name: "NumpadDivide", keys: ["/"]},
                    {name: "NumpadMultiply", keys: ["*"]},
                    {name: "NumpadSubtract", keys: ["-"]}
                ],
            },
            {
                row: [
                    {name: "Numpad7", keys: ["7", "Home"]},
                    {
                        name: "Numpad8", keys: ["8",
                            <FaChevronUp />]
                    },
                    {name: "Numpad9", keys: ["9", "Pg\nUp"]},
                    {name: "NumpadAdd", keys: ["+"]} 
                    /* removed merge class, CSS grid-row: span 2 handles this */
                ]
            },
            {
                row: [
                    {
                        name: "Numpad4", keys: ["4",
                            <FaChevronLeft />]
                    },
                    {name: "Numpad5", keys: ["5"]},
                    {
                        name: "Numpad6", keys: ["6",
                            <FaChevronRight />]
                    },
                    /* Removed Duplicate NumpadAdd here. Grid allows the previous row's Key to hang down */
                ]
            },
            {
                row: [
                    {name: "Numpad1", keys: ["1", "End"]},
                    {
                        name: "Numpad2", keys: ["2",
                            <FaChevronDown />]
                    },
                    {name: "Numpad3", keys: ["3", "Pg\nDn"]},
                    {name: "NumpadEnter", keys: ["Enter"]},
                     /* removed merge class, CSS grid-row: span 2 handles this */
                ]
            },
            {
                row: [
                    {name: "Numpad0", keys: ["0", "Ins"]}, /* removed merge class, CSS grid-column: span 2 handles this */
                    /* Removed Duplicate Numpad0 here */
                    {name: "NumpadDecimal", keys: [".", "Del"]},
                    /* Removed Duplicate NumpadEnter here */
                ]
            },
        ]
    }
]
const tenKeylessKeys = [
    {
        section: [
            {
                row: [
                    {name: "Escape", keys: ["Esc"]},
                    {name: "F1", keys: ["F1"]},
                    {name: "F2", keys: ["F2"]},
                    {name: "F3", keys: ["F3"]},
                    {name: "F4", keys: ["F4"]},
                    {name: "F5", keys: ["F5"]},
                    {name: "F6", keys: ["F6"]},
                    {name: "F7", keys: ["F7"]},
                    {name: "F8", keys: ["F8"]},
                    {name: "F9", keys: ["F9"]},
                    {name: "F10", keys: ["F10"]},
                    {name: "F11", keys: ["F11"]},
                    {name: "F12", keys: ["F12"]},
                ]
            },
            {
                row: [
                    {name: "Backquote", keys: ["`", "~"]},
                    {name: "Digit1", keys: ["1", "!"]},
                    {name: "Digit2", keys: ["2", "@"]},
                    {name: "Digit3", keys: ["3", "#"]},
                    {name: "Digit4", keys: ["4", "$"]},
                    {name: "Digit5", keys: ["5", "%"]},
                    {name: "Digit6", keys: ["6", "^"]},
                    {name: "Digit7", keys: ["7", "&"]},
                    {name: "Digit8", keys: ["8", "*"]},
                    {name: "Digit9", keys: ["9", "("]},
                    {name: "Digit0", keys: ["0", ")"]},
                    {name: "Minus", keys: ["-", "_"]},
                    {name: "Equal", keys: ["=", "+"]},
                    {name: "Backspace", keys: ["Backspace"]},
                ]
            },
            {
                row: [
                    {name: "Tab", keys: ["Tab"]},
                    {name: "KeyQ", keys: ["q"]},
                    {name: "KeyW", keys: ["w"]},
                    {name: "KeyE", keys: ["e"]},
                    {name: "KeyR", keys: ["r"]},
                    {name: "KeyT", keys: ["t"]},
                    {name: "KeyY", keys: ["y"]},
                    {name: "KeyU", keys: ["u"]},
                    {name: "KeyI", keys: ["i"]},
                    {name: "KeyO", keys: ["o"]},
                    {name: "KeyP", keys: ["p"]},
                    {name: "BracketLeft", keys: ["[", "{"]},
                    {name: "BracketRight", keys: ["]", "}"]},
                    {name: "Backslash", keys: ["\\", "|"]},
                ]
            },
            {
                row: [
                    {name: "CapsLock", keys: ["Caps"]},
                    {name: "KeyA", keys: ["a"]},
                    {name: "KeyS", keys: ["s"]},
                    {name: "KeyD", keys: ["d"]},
                    {name: "KeyF", keys: ["f"]},
                    {name: "KeyG", keys: ["g"]},
                    {name: "KeyH", keys: ["h"]},
                    {name: "KeyJ", keys: ["j"]},
                    {name: "KeyK", keys: ["k"]},
                    {name: "KeyL", keys: ["l"]},
                    {name: "Semicolon", keys: [";", ":"]},
                    {name: "Quote", keys: ["'", "\""]},
                    {name: "Enter", keys: ["Enter"]}
                ]
            },
            {
                row: [
                    {name: "ShiftLeft", keys: ["Shift"]},
                    {name: "KeyZ", keys: ["z"]},
                    {name: "KeyX", keys: ["x"]},
                    {name: "KeyC", keys: ["c"]},
                    {name: "KeyV", keys: ["v"]},
                    {name: "KeyB", keys: ["b"]},
                    {name: "KeyN", keys: ["n"]},
                    {name: "KeyM", keys: ["m"]},
                    {name: "Comma", keys: [",", "<"]},
                    {name: "Period", keys: [".", ">"]},
                    {name: "Slash", keys: ["/", "?"]},
                    {name: "ShiftRight", keys: ["Shift"]}
                ]
            },
            {
                row: [
                    {name: "ControlLeft", keys: ["Ctrl"]},
                    {
                        name: "MetaLeft", keys: [
                            <FaWindows />], special: true, excluded: true
                    },
                    {name: "AltLeft", keys: ["Alt"]},
                    {name: "Space", keys: ["Space"]},
                    {
                        name: "MetaRight", keys: [
                            <FaWindows />], special: true, excluded: true
                    },
                    {name: "AltRight", keys: ["Alt"]},
                    {
                        name: "ContextMenu", keys: [
                            <CgMenuBoxed />], special: true
                    },
                    {name: "ControlRight", keys: ["Ctrl"]},
                ]
            },
        ]
    },
    {
        section: [
            {
                row: [
                    {name: "PrintScreen", keys: ["Prt Sc", "Sys Rq"]},
                    {name: "ScrollLock", keys: ["Scr Lk"]},
                    {name: "Pause", keys: ["Pause"]},
                ],
            },
            {
                row: [
                    {name: "Insert", keys: ["Ins"]},
                    {name: "Home", keys: ["Home"]},
                    {name: "PageUp", keys: ["Page\nUp"]},
                ]
            },
            {
                row: [
                    {name: "Delete", keys: ["Del"]},
                    {name: "End", keys: ["End"]},
                    {name: "PageDown", keys: ["Page\nDown"]},
                ]
            },
            {
                row: [
                    {name: "SPACER", keys: ["spc"], excluded: true},
                    {name: "SPACER", keys: ["spc"], excluded: true},
                    {name: "SPACER", keys: ["spc"], excluded: true},
                ]
            },
            {
                row: [
                    {name: "SPACER", keys: ["spc"], excluded: true},
                    {
                        name: "ArrowUp", keys: [
                            <FaChevronUp />]
                    },
                    {name: "SPACER", keys: ["spc"], excluded: true},
                ]
            },
            {
                row: [
                    {
                        name: "ArrowLeft", keys: [
                            <FaChevronLeft />]
                    },
                    {
                        name: "ArrowDown", keys: [
                            <FaChevronDown />]
                    },
                    {
                        name: "ArrowRight", keys: [
                            <FaChevronRight />]
                    },
                ]
            },
        ]
    }
]
const compactKeys = [
    {
        section: [
            {
                row: [
                    {name: "Escape", keys: ["Esc"]},
                    {name: "Digit1", keys: ["1", "!"]},
                    {name: "Digit2", keys: ["2", "@"]},
                    {name: "Digit3", keys: ["3", "#"]},
                    {name: "Digit4", keys: ["4", "$"]},
                    {name: "Digit5", keys: ["5", "%"]},
                    {name: "Digit6", keys: ["6", "^"]},
                    {name: "Digit7", keys: ["7", "&"]},
                    {name: "Digit8", keys: ["8", "*"]},
                    {name: "Digit9", keys: ["9", "("]},
                    {name: "Digit0", keys: ["0", ")"]},
                    {name: "Minus", keys: ["-", "_"]},
                    {name: "Equal", keys: ["=", "+"]},
                    {name: "Backspace", keys: ["Backspace"]},
                ]
            },
            {
                row: [
                    {name: "Tab", keys: ["Tab"]},
                    {name: "KeyQ", keys: ["q"]},
                    {name: "KeyW", keys: ["w"]},
                    {name: "KeyE", keys: ["e"]},
                    {name: "KeyR", keys: ["r"]},
                    {name: "KeyT", keys: ["t"]},
                    {name: "KeyY", keys: ["y"]},
                    {name: "KeyU", keys: ["u"]},
                    {name: "KeyI", keys: ["i"]},
                    {name: "KeyO", keys: ["o"]},
                    {name: "KeyP", keys: ["p"]},
                    {name: "BracketLeft", keys: ["[", "{"]},
                    {name: "BracketRight", keys: ["]", "}"]},
                    {name: "Backslash", keys: ["\\", "|"]},
                ]
            },
            {
                row: [
                    {name: "CapsLock", keys: ["Caps"]},
                    {name: "KeyA", keys: ["a"]},
                    {name: "KeyS", keys: ["s"]},
                    {name: "KeyD", keys: ["d"]},
                    {name: "KeyF", keys: ["f"]},
                    {name: "KeyG", keys: ["g"]},
                    {name: "KeyH", keys: ["h"]},
                    {name: "KeyJ", keys: ["j"]},
                    {name: "KeyK", keys: ["k"]},
                    {name: "KeyL", keys: ["l"]},
                    {name: "Semicolon", keys: [";", ":"]},
                    {name: "Quote", keys: ["'", "\""]},
                    {name: "Enter", keys: ["Enter"]}
                ]
            },
            {
                row: [
                    {name: "ShiftLeft", keys: ["Shift"]},
                    {name: "KeyZ", keys: ["z"]},
                    {name: "KeyX", keys: ["x"]},
                    {name: "KeyC", keys: ["c"]},
                    {name: "KeyV", keys: ["v"]},
                    {name: "KeyB", keys: ["b"]},
                    {name: "KeyN", keys: ["n"]},
                    {name: "KeyM", keys: ["m"]},
                    {name: "Comma", keys: [",", "<"]},
                    {name: "Period", keys: [".", ">"]},
                    {name: "Slash", keys: ["/", "?"]},
                    {name: "ShiftRight", keys: ["Shift"]}
                ]
            },
            {
                row: [
                    {name: "ControlLeft", keys: ["Ctrl"]},
                    {
                        name: "MetaLeft", keys: [
                            <FaWindows />], special: true, excluded: true
                    },
                    {name: "AltLeft", keys: ["Alt"]},
                    {name: "Space", keys: ["Space"]},
                    {name: "AltRight", keys: ["Alt"]},
                    {
                        name: "ContextMenu", keys: [
                            <CgMenuBoxed />], special: true
                    },
                    {name: "ControlRight", keys: ["Ctrl"]},
                ]
            },
        ]
    }
]
const cloneKeyboardKeys = (keys) => keys.map((keyboardSection) => ({
    ...keyboardSection,
    section: keyboardSection.section.map((section) => ({
        ...section,
        row: section.row.map((row) => ({...row}))
    }))
}));

const findKeyboardRow = (keys, keyName) => {
    if (keyName === "FnRight") return {name: "FnRight", keys: ["Fn"], excluded: true};
    for (const keyboardSection of keys) {
        for (const section of keyboardSection.section) {
            const row = section.row.find((item) => item.name === keyName);
            if (row) return row;
        }
    }
    return null;
};

const makeKeyboardRowsFromNames = (sourceKeys, keyRows) => [{
    section: keyRows.map((keyRow) => ({
        row: keyRow.map((keyName) => ({...findKeyboardRow(sourceKeys, keyName)}))
    }))
}];

const NINETY_SIX_KEY_ROWS = [
    ["Escape", "F1", "F2", "F3", "F4", "F5", "F6", "F7", "F8", "F9", "F10", "F11", "F12", "PrintScreen", "Delete", "Home", "End", "PageUp", "PageDown"],
    ["Backquote", "Digit1", "Digit2", "Digit3", "Digit4", "Digit5", "Digit6", "Digit7", "Digit8", "Digit9", "Digit0", "Minus", "Equal", "Backspace", "NumLock", "NumpadDivide", "NumpadMultiply", "NumpadSubtract"],
    ["Tab", "KeyQ", "KeyW", "KeyE", "KeyR", "KeyT", "KeyY", "KeyU", "KeyI", "KeyO", "KeyP", "BracketLeft", "BracketRight", "Backslash", "Numpad7", "Numpad8", "Numpad9", "NumpadAdd"],
    ["CapsLock", "KeyA", "KeyS", "KeyD", "KeyF", "KeyG", "KeyH", "KeyJ", "KeyK", "KeyL", "Semicolon", "Quote", "Enter", "Numpad4", "Numpad5", "Numpad6"],
    ["ShiftLeft", "KeyZ", "KeyX", "KeyC", "KeyV", "KeyB", "KeyN", "KeyM", "Comma", "Period", "Slash", "ShiftRight", "ArrowUp", "Numpad1", "Numpad2", "Numpad3", "NumpadEnter"],
    ["ControlLeft", "MetaLeft", "AltLeft", "Space", "MetaRight", "ContextMenu", "AltRight", "ArrowLeft", "ArrowDown", "ArrowRight", "Numpad0", "NumpadDecimal"],
];
const NINETY_SIX_KEY_ORDER = NINETY_SIX_KEY_ROWS.flat();

const SEVENTY_FIVE_EXPLODED_KEY_ROWS = [
    ["Escape", "F1", "F2", "F3", "F4", "F5", "F6", "F7", "F8", "F9", "F10", "F11", "F12", "Delete", "Insert"],
    ["Backquote", "Digit1", "Digit2", "Digit3", "Digit4", "Digit5", "Digit6", "Digit7", "Digit8", "Digit9", "Digit0", "Minus", "Equal", "Backspace", "End"],
    ["Tab", "KeyQ", "KeyW", "KeyE", "KeyR", "KeyT", "KeyY", "KeyU", "KeyI", "KeyO", "KeyP", "BracketLeft", "BracketRight", "Backslash", "PageUp"],
    ["CapsLock", "KeyA", "KeyS", "KeyD", "KeyF", "KeyG", "KeyH", "KeyJ", "KeyK", "KeyL", "Semicolon", "Quote", "Enter", "PageDown"],
    ["ShiftLeft", "KeyZ", "KeyX", "KeyC", "KeyV", "KeyB", "KeyN", "KeyM", "Comma", "Period", "Slash", "ShiftRight", "ArrowUp"],
    ["ControlLeft", "MetaLeft", "AltLeft", "Space", "AltRight", "ContextMenu", "ControlRight", "ArrowLeft", "ArrowRight"],
    ["ArrowDown"],
];
const SEVENTY_FIVE_EXPLODED_KEY_ORDER = SEVENTY_FIVE_EXPLODED_KEY_ROWS.flat();

const SEVENTY_FIVE_COMPACT_KEY_ROWS = [
    ["Escape", "F1", "F2", "F3", "F4", "F5", "F6", "F7", "F8", "F9", "F10", "F11", "F12", "PrintScreen", "Insert", "Delete"],
    ["Backquote", "Digit1", "Digit2", "Digit3", "Digit4", "Digit5", "Digit6", "Digit7", "Digit8", "Digit9", "Digit0", "Minus", "Equal", "Backspace", "Home"],
    ["Tab", "KeyQ", "KeyW", "KeyE", "KeyR", "KeyT", "KeyY", "KeyU", "KeyI", "KeyO", "KeyP", "BracketLeft", "BracketRight", "Backslash", "End"],
    ["CapsLock", "KeyA", "KeyS", "KeyD", "KeyF", "KeyG", "KeyH", "KeyJ", "KeyK", "KeyL", "Semicolon", "Quote", "Enter", "PageUp"],
    ["ShiftLeft", "KeyZ", "KeyX", "KeyC", "KeyV", "KeyB", "KeyN", "KeyM", "Comma", "Period", "Slash", "ShiftRight", "ArrowUp", "PageDown"],
    ["ControlLeft", "AltLeft", "MetaLeft", "Space", "MetaRight", "ContextMenu", "AltRight", "ArrowLeft", "ArrowDown", "ArrowRight"],
];
const SEVENTY_FIVE_COMPACT_KEY_ORDER = SEVENTY_FIVE_COMPACT_KEY_ROWS.flat();

const SIXTY_FIVE_EXPLODED_KEY_ROWS = [
    ["Escape", "Digit1", "Digit2", "Digit3", "Digit4", "Digit5", "Digit6", "Digit7", "Digit8", "Digit9", "Digit0", "Minus", "Equal", "Backquote", "Delete"],
    ["Tab", "KeyQ", "KeyW", "KeyE", "KeyR", "KeyT", "KeyY", "KeyU", "KeyI", "KeyO", "KeyP", "BracketLeft", "BracketRight", "Backspace", "PageUp"],
    ["CapsLock", "KeyA", "KeyS", "KeyD", "KeyF", "KeyG", "KeyH", "KeyJ", "KeyK", "KeyL", "Semicolon", "Quote", "Enter", "PageDown"],
    ["ShiftLeft", "KeyZ", "KeyX", "KeyC", "KeyV", "KeyB", "KeyN", "KeyM", "Comma", "Period", "Slash", "ShiftRight"],
    ["ArrowUp"],
    ["ControlLeft", "MetaLeft", "AltLeft", "Space", "AltRight", "ControlRight", "ContextMenu"],
    ["ArrowLeft", "ArrowDown", "ArrowRight"],
];
const SIXTY_FIVE_EXPLODED_KEY_ORDER = SIXTY_FIVE_EXPLODED_KEY_ROWS.flat();

const SIXTY_FIVE_COMPACT_KEY_ROWS = [
    ["Escape", "Digit1", "Digit2", "Digit3", "Digit4", "Digit5", "Digit6", "Digit7", "Digit8", "Digit9", "Digit0", "Minus", "Equal", "Backspace", "Delete"],
    ["Tab", "KeyQ", "KeyW", "KeyE", "KeyR", "KeyT", "KeyY", "KeyU", "KeyI", "KeyO", "KeyP", "BracketLeft", "BracketRight", "Backslash", "Home"],
    ["CapsLock", "KeyA", "KeyS", "KeyD", "KeyF", "KeyG", "KeyH", "KeyJ", "KeyK", "KeyL", "Semicolon", "Quote", "Enter", "PageUp"],
    ["ShiftLeft", "KeyZ", "KeyX", "KeyC", "KeyV", "KeyB", "KeyN", "KeyM", "Comma", "Period", "Slash", "ShiftRight", "ArrowUp", "PageDown"],
    ["ControlLeft", "AltLeft", "MetaLeft", "Space", "MetaRight", "ContextMenu", "AltRight", "ArrowLeft", "ArrowDown", "ArrowRight"],
];
const SIXTY_FIVE_COMPACT_KEY_ORDER = SIXTY_FIVE_COMPACT_KEY_ROWS.flat();

const COMPACT_KEY_ROWS = [
    ["Escape", "Digit1", "Digit2", "Digit3", "Digit4", "Digit5", "Digit6", "Digit7", "Digit8", "Digit9", "Digit0", "Minus", "Equal", "Backspace"],
    ["Tab", "KeyQ", "KeyW", "KeyE", "KeyR", "KeyT", "KeyY", "KeyU", "KeyI", "KeyO", "KeyP", "BracketLeft", "BracketRight", "Backslash"],
    ["CapsLock", "KeyA", "KeyS", "KeyD", "KeyF", "KeyG", "KeyH", "KeyJ", "KeyK", "KeyL", "Semicolon", "Quote", "Enter"],
    ["ShiftLeft", "KeyZ", "KeyX", "KeyC", "KeyV", "KeyB", "KeyN", "KeyM", "Comma", "Period", "Slash", "ShiftRight"],
    ["ControlLeft", "MetaLeft", "AltLeft", "Space", "AltRight", "ContextMenu", "ControlRight", "FnRight"],
];
const COMPACT_KEY_ORDER = COMPACT_KEY_ROWS.flat();

const FIFTY_KEY_ROWS = [
    ["Escape", "KeyQ", "KeyW", "KeyE", "KeyR", "KeyT", "KeyY", "KeyU", "KeyI", "KeyO", "KeyP", "BracketLeft", "BracketRight", "Backspace"],
    ["Tab", "KeyA", "KeyS", "KeyD", "KeyF", "KeyG", "KeyH", "KeyJ", "KeyK", "KeyL", "Semicolon", "Quote", "Enter"],
    ["ShiftLeft", "KeyZ", "KeyX", "KeyC", "KeyV", "KeyB", "KeyN", "KeyM", "Comma", "Period", "Slash", "ShiftRight", "FnRight"],
    ["ControlLeft", "AltLeft", "ArrowUp", "Space", "ArrowDown", "AltRight", "ContextMenu", "ControlRight"],
];
const FIFTY_KEY_ORDER = FIFTY_KEY_ROWS.flat();

const FORTY_KEY_ROWS = [
    ["Escape", "KeyQ", "KeyW", "KeyE", "KeyR", "KeyT", "KeyY", "KeyU", "KeyI", "KeyO", "KeyP", "Backspace"],
    ["Tab", "KeyA", "KeyS", "KeyD", "KeyF", "KeyG", "KeyH", "KeyJ", "KeyK", "KeyL", "Enter"],
    ["ShiftLeft", "KeyZ", "KeyX", "KeyC", "KeyV", "KeyB", "KeyN", "KeyM", "Slash", "ShiftRight", "FnRight"],
    ["ControlLeft", "MetaLeft", "AltLeft", "Space", "AltRight", "ContextMenu", "ControlRight"],
];
const FORTY_KEY_ORDER = FORTY_KEY_ROWS.flat();

const makeSeventyFiveKeys = () => {
    return makeKeyboardRowsFromNames(fullSizeKeys, SEVENTY_FIVE_COMPACT_KEY_ROWS);
};

const makeSeventyFiveExplodedKeys = () => {
    return makeKeyboardRowsFromNames(fullSizeKeys, SEVENTY_FIVE_EXPLODED_KEY_ROWS);
};

const makeSixtyFiveExplodedKeys = () => makeKeyboardRowsFromNames(fullSizeKeys, SIXTY_FIVE_EXPLODED_KEY_ROWS);

const makeSixtyFiveKeys = () => makeKeyboardRowsFromNames(fullSizeKeys, SIXTY_FIVE_COMPACT_KEY_ROWS);

const makeCompactKeys = () => makeKeyboardRowsFromNames(fullSizeKeys, COMPACT_KEY_ROWS);

const makeFiftyKeys = () => makeKeyboardRowsFromNames(fullSizeKeys, FIFTY_KEY_ROWS);

const makeFortyKeys = () => makeKeyboardRowsFromNames(fullSizeKeys, FORTY_KEY_ROWS);

const makeNinetySixKeys = () => {
    return makeKeyboardRowsFromNames(fullSizeKeys, NINETY_SIX_KEY_ROWS);
};

const makeEighteenHundredKeys = () => {
    const keys = cloneKeyboardKeys(fullSizeKeys);
    keys[1] = {
        section: [
            {row: [
                {name: "SPACER", keys: ["spc"], excluded: true},
                {name: "ArrowUp", keys: [<FaChevronUp />]},
                {name: "SPACER", keys: ["spc"], excluded: true},
            ]},
            {row: [
                {name: "ArrowLeft", keys: [<FaChevronLeft />]},
                {name: "ArrowDown", keys: [<FaChevronDown />]},
                {name: "ArrowRight", keys: [<FaChevronRight />]},
            ]},
        ]
    };
    return keys;
};

const seventyFiveKeys = makeSeventyFiveKeys();
const seventyFiveExplodedKeys = makeSeventyFiveExplodedKeys();
const sixtyFiveExplodedKeys = makeSixtyFiveExplodedKeys();
const sixtyFiveKeys = makeSixtyFiveKeys();
const compactLayoutKeys = makeCompactKeys();
const eighteenHundredKeys = makeEighteenHundredKeys();
const ninetySixKeys = makeNinetySixKeys();
const fiftyKeys = makeFiftyKeys();
const fortyKeys = makeFortyKeys();
const FULL_SIZE_SILHOUETTE_WIDTH = 560.67;
const KEYBOARD_KEY_POSITION = {
    Escape: [0, 0],
    F1: [0, 2],
    F2: [0, 3],
    F3: [0, 4],
    F4: [0, 5],
    F5: [0, 7],
    F6: [0, 8],
    F7: [0, 9],
    F8: [0, 10],
    F9: [0, 12],
    F10: [0, 13],
    F11: [0, 14],
    F12: [0, 15],
    PrintScreen: [0, 17],
    ScrollLock: [0, 18],
    Pause: [0, 19],
    NumLock: [0, 21],
    NumpadDivide: [0, 22],
    NumpadMultiply: [0, 23],
    NumpadSubtract: [0, 24],
    Backquote: [1, 0],
    Digit1: [1, 1],
    Digit2: [1, 2],
    Digit3: [1, 3],
    Digit4: [1, 4],
    Digit5: [1, 5],
    Digit6: [1, 6],
    Digit7: [1, 7],
    Digit8: [1, 8],
    Digit9: [1, 9],
    Digit0: [1, 10],
    Minus: [1, 11],
    Equal: [1, 12],
    Backspace: [1, 13],
    Insert: [1, 17],
    Home: [1, 18],
    PageUp: [1, 19],
    Numpad7: [1, 21],
    Numpad8: [1, 22],
    Numpad9: [1, 23],
    NumpadAdd: [1, 24],
    Tab: [2, 0],
    KeyQ: [2, 1],
    KeyW: [2, 2],
    KeyE: [2, 3],
    KeyR: [2, 4],
    KeyT: [2, 5],
    KeyY: [2, 6],
    KeyU: [2, 7],
    KeyI: [2, 8],
    KeyO: [2, 9],
    KeyP: [2, 10],
    BracketLeft: [2, 11],
    BracketRight: [2, 12],
    Backslash: [2, 13],
    Delete: [0, 16],
    End: [2, 18],
    PageDown: [2, 19],
    Numpad4: [2, 21],
    Numpad5: [2, 22],
    Numpad6: [2, 23],
    CapsLock: [3, 0],
    KeyA: [3, 1],
    KeyS: [3, 2],
    KeyD: [3, 3],
    KeyF: [3, 4],
    KeyG: [3, 5],
    KeyH: [3, 6],
    KeyJ: [3, 7],
    KeyK: [3, 8],
    KeyL: [3, 9],
    Semicolon: [3, 10],
    Quote: [3, 11],
    Enter: [3, 12],
    Numpad1: [3, 21],
    Numpad2: [3, 22],
    Numpad3: [3, 23],
    NumpadEnter: [3, 24],
    ShiftLeft: [4, 0],
    KeyZ: [4, 1],
    KeyX: [4, 2],
    KeyC: [4, 3],
    KeyV: [4, 4],
    KeyB: [4, 5],
    KeyN: [4, 6],
    KeyM: [4, 7],
    Comma: [4, 8],
    Period: [4, 9],
    Slash: [4, 10],
    ShiftRight: [4, 12],
    ArrowUp: [4, 18],
    Numpad0: [4, 21],
    NumpadDecimal: [4, 23],
    ControlLeft: [5, 0],
    MetaLeft: [5, 1],
    AltLeft: [5, 2],
    Space: [5, 5],
    MetaRight: [5, 8],
    AltRight: [5, 9],
    Fn: [5, 10],
    ContextMenu: [5, 10],
    ControlRight: [5, 11],
    FnRight: [5, 12],
    ArrowLeft: [5, 17],
    ArrowDown: [5, 18],
    ArrowRight: [5, 19],
};

const getKeyboardKeyPosition = (keyName) => KEYBOARD_KEY_POSITION[keyName] || [99, 99];

const getPhysicalKeyboardRows = (sections) => (
    sections?.flatMap((keyboardSection) => (
        keyboardSection?.section?.flatMap((section) => section?.row || []) || []
    )) || []
)
    .filter((row) => row?.name && row.name !== "SPACER")
    .sort((a, b) => {
        const [rowA, columnA] = getKeyboardKeyPosition(a.name);
        const [rowB, columnB] = getKeyboardKeyPosition(b.name);
        return rowA - rowB || columnA - columnB;
    });

const sortSilhouetteKeys = (keys) => {
    const rows = [];
    [...(keys || [])]
        .sort((a, b) => parseFloat(a.top) - parseFloat(b.top) || parseFloat(a.left) - parseFloat(b.left))
        .forEach((key) => {
            const top = parseFloat(key.top);
            const row = rows.find((candidate) => Math.abs(candidate.top - top) < 3);
            if (row) {
                row.keys.push(key);
                row.top = Math.min(row.top, top);
            } else {
                rows.push({top, keys: [key]});
            }
        });

    return rows
        .sort((a, b) => a.top - b.top)
        .flatMap((row) => row.keys.sort((a, b) => parseFloat(a.left) - parseFloat(b.left)));
};

const EIGHTEEN_HUNDRED_KEY_ORDER = [
    "Escape", "F1", "F2", "F3", "F4", "F5", "F6", "F7", "F8", "F9", "F10", "F11", "F12",
    "Backquote", "Digit1", "Digit2", "Digit3", "Digit4", "Digit5", "Digit6", "Digit7", "Digit8", "Digit9", "Digit0", "Minus", "Equal", "Backspace", "NumLock", "NumpadDivide", "NumpadMultiply", "NumpadSubtract",
    "Tab", "KeyQ", "KeyW", "KeyE", "KeyR", "KeyT", "KeyY", "KeyU", "KeyI", "KeyO", "KeyP", "BracketLeft", "BracketRight", "Backslash", "Numpad7", "Numpad8", "Numpad9", "NumpadAdd",
    "CapsLock", "KeyA", "KeyS", "KeyD", "KeyF", "KeyG", "KeyH", "KeyJ", "KeyK", "KeyL", "Semicolon", "Quote", "Enter", "Numpad4", "Numpad5", "Numpad6",
    "ShiftLeft", "KeyZ", "KeyX", "KeyC", "KeyV", "KeyB", "KeyN", "KeyM", "Comma", "Period", "Slash", "ShiftRight", "Numpad1", "Numpad2", "Numpad3", "NumpadEnter",
    "ArrowUp",
    "ControlLeft", "MetaLeft", "AltLeft", "Space", "AltRight", "ContextMenu", "ControlRight", "Numpad0", "NumpadDecimal",
    "ArrowLeft", "ArrowDown", "ArrowRight",
];

const getEighteenHundredSilhouetteKeys = (keys) => sortSilhouetteKeys(keys).filter((_, index) => ![13, 14, 15, 16, 17].includes(index));

const FULL_SIZE_KEY_ORDER = [
    "Escape", "F1", "F2", "F3", "F4", "F5", "F6", "F7", "F8", "F9", "F10", "F11", "F12", "PrintScreen", "ScrollLock", "Pause",
    "Backquote", "Digit1", "Digit2", "Digit3", "Digit4", "Digit5", "Digit6", "Digit7", "Digit8", "Digit9", "Digit0", "Minus", "Equal", "Backspace", "Insert", "Home", "PageUp", "NumLock", "NumpadDivide", "NumpadMultiply", "NumpadSubtract",
    "Tab", "KeyQ", "KeyW", "KeyE", "KeyR", "KeyT", "KeyY", "KeyU", "KeyI", "KeyO", "KeyP", "BracketLeft", "BracketRight", "Backslash", "Delete", "End", "PageDown", "Numpad7", "Numpad8", "Numpad9", "NumpadAdd",
    "CapsLock", "KeyA", "KeyS", "KeyD", "KeyF", "KeyG", "KeyH", "KeyJ", "KeyK", "KeyL", "Semicolon", "Quote", "Enter", "Numpad4", "Numpad5", "Numpad6",
    "ShiftLeft", "KeyZ", "KeyX", "KeyC", "KeyV", "KeyB", "KeyN", "KeyM", "Comma", "Period", "Slash", "ShiftRight", "ArrowUp", "Numpad1", "Numpad2", "Numpad3", "NumpadEnter",
    "ControlLeft", "MetaLeft", "AltLeft", "Space", "MetaRight", "AltRight", "ContextMenu", "ControlRight", "ArrowLeft", "ArrowDown", "ArrowRight", "Numpad0", "NumpadDecimal",
];

const TENKEYLESS_KEY_ORDER = [
    "Escape", "F1", "F2", "F3", "F4", "F5", "F6", "F7", "F8", "F9", "F10", "F11", "F12", "PrintScreen", "ScrollLock", "Pause",
    "Backquote", "Digit1", "Digit2", "Digit3", "Digit4", "Digit5", "Digit6", "Digit7", "Digit8", "Digit9", "Digit0", "Minus", "Equal", "Backspace", "Insert", "Home", "PageUp",
    "Tab", "KeyQ", "KeyW", "KeyE", "KeyR", "KeyT", "KeyY", "KeyU", "KeyI", "KeyO", "KeyP", "BracketLeft", "BracketRight", "Backslash", "Delete", "End", "PageDown",
    "CapsLock", "KeyA", "KeyS", "KeyD", "KeyF", "KeyG", "KeyH", "KeyJ", "KeyK", "KeyL", "Semicolon", "Quote", "Enter",
    "ShiftLeft", "KeyZ", "KeyX", "KeyC", "KeyV", "KeyB", "KeyN", "KeyM", "Comma", "Period", "Slash", "ShiftRight", "ArrowUp",
    "ControlLeft", "MetaLeft", "AltLeft", "Space", "MetaRight", "AltRight", "ContextMenu", "ControlRight", "ArrowLeft", "ArrowDown", "ArrowRight",
];

const mapStylesByKeyOrder = (keyOrder, silhouetteKeys) => keyOrder.reduce((styleMap, keyName, index) => {
    const silhouetteKey = silhouetteKeys[index];
    if (silhouetteKey) styleMap[keyName] = silhouetteKey;
    if (keyName === "ContextMenu") styleMap.Fn = silhouetteKey;
    return styleMap;
}, {});

const KeyboardTest = () => {
    const showMode = false;
    const {keyboard, updateKeyboard, showModal} = useKeyboardState();
    const activeKeyboardType = keyboard && keyboard?.types?.filter(item => item?.active)[0];
    const activeKeyboardLayout = activeKeyboardType?.layout || activeKeyboardType?.name || "";
    const keyboardLayoutCoordinates = {
        ...keyboardSilhouetteLayouts,
        ...keyboardCustomLayouts,
    };
    const activeSilhouetteLayout = ["fullsize", "eighteenhundred", "ninetysix", "tenkeyless", "seventyfive-exploded", "seventyfive", "sixtyfive-exploded", "sixtyfive", "compact", "fifty", "forty"].includes(activeKeyboardLayout)
        ? keyboardLayoutCoordinates[activeKeyboardLayout]
        : undefined;
    const [percentage, setPercentage] = useState(0)
    const completionModalShownRef = React.useRef(false);
    const specialKeysMapping = {
        // lwin: 'MetaLeft',
        // prtscrn: 'PrintScreen',
        // apps: 'ContextMenu'
    }

    const activeHasBuiltInFnKey = hasKeyName(activeKeyboardType?.keys, "FnRight");
    const activeHasSwitchableFnKey = hasKeyName(activeKeyboardType?.keys, "Fn");

    const setBottomRowVariant = (variant) => {
        if (!keyboard?.types?.length || !activeKeyboardType) return;
        const resolvedVariant = activeHasBuiltInFnKey && variant === "fn" ? "menu" : variant;

        updateKeyboard({
            keyboard: {
                ...keyboard,
                types: keyboard.types.map((item) => {
                    if (!item.active) return item;
                    return {
                        ...item,
                        bottomRowVariant: resolvedVariant,
                        keys: mapSwitchableBottomRows(item.keys, resolvedVariant),
                        results: mapSwitchableBottomRows(item.results, resolvedVariant),
                    };
                })
            }
        });
    };

    const identifKeyboard = (name) => keyboard && keyboard?.types?.filter(item => item.name === name)[0]
    const message = () => {
        const failed = findFailedKeys(activeKeyboardType?.results, 'failed')?.some(f => f);
        showModal({
            title: "Keyboard Test",
            status: failed ? "failed" : "passed",
            body: failed
                ? "Test finished with status: Failed. Please Note: When/if keyboard test progress reaches 100% this means every key was successfully captured from your keyboard."
                : "Test finished with status: Passed. Please Note: When/if keyboard test progress reaches 100% this means every key was successfully captured from your keyboard."
        });
    }//Message on success
    const messageForSpecialKeys = () => {
        showModal({
            title: "Please Note",
            status: "info",
            body: "Windows (Win/Meta) and Fn keys cannot be detected in a browser — browsers never receive Fn key events. Those keys are optional. Press other keys one at a time to complete the test. Context Menu may work depending on your browser."
        });
    }
    const specialKeysWerePressed = (someOrEvery = "every") => {
        let pressedSpecKeys = Object.keys(specialKeysMapping).filter(key => specialKeysMapping[key] && specialKeysMapping[key]).map(key => specialKeysMapping[key]);
        let boolArray = pressedSpecKeys && pressedSpecKeys?.map(speciaKey => {
            return deepSearch(activeKeyboardType, 'name', (k, v) => v === speciaKey)?.wasPressed;
        })
        //console.log(boolArray?.every(x => x === true))
        if (someOrEvery === "every") return !!boolArray?.every(x => x === true); //If we need all keys to be true
        return !!boolArray?.some(x => x === true); //If we need if at least one key to be true

    }//Check if all special keys were clicked and captured
    const specialKeyIsPressed = () => {
        let pressedSpecKeys = Object.keys(specialKeysMapping).filter(key => specialKeysMapping[key] && specialKeysMapping[key]).map(key => specialKeysMapping[key]);
        let boolArray = pressedSpecKeys && pressedSpecKeys?.map(speciaKey => {
            return deepSearch(activeKeyboardType, 'name', (k, v) => v === speciaKey)?.isPressed;
        })
        //console.log(boolArray?.some(x => x === true))
        return boolArray?.filter(x => x === true);

    }//Check if all special keys were clicked and captured
    const checkStatus = () => {
        // No-op in the browser: special keys (Windows/Meta) cannot be reliably
        // captured without a native/Electron helper, so there is no server to poll.
    } //Check status of special keys controled by server (no-op in browser)
    const isWalkableValue = (value) => {
        if (value === null || typeof value !== "object") return false;
        if (React.isValidElement(value)) return false;
        if (Array.isArray(value)) return true;
        const proto = Object.getPrototypeOf(value);
        return proto === Object.prototype || proto === null;
    };
    const deepSearch = (object, key, predicate) => {
        if (!isWalkableValue(object)) return null;
        if (Object.prototype.hasOwnProperty.call(object, key) && predicate(key, object[key]) === true) return object;
        for (const value of Object.values(object)) {
            if (!isWalkableValue(value)) continue;
            const found = deepSearch(value, key, predicate);
            if (found != null) return found;
        }
        return null;
    } // search in activeKeyboardType object to find out if special key wasPressed
    const keyAmount = (alreadyisPressed = false) => {
        let keyCount = activeKeyboardType?.keys.reduce((count, sections) => count + "," + sections.section.map((row) => {
            if (!alreadyisPressed) {
                return row.row.filter(item => item.keys.length > 0 && !item.excluded).length
            } else {
                return row.row.filter(item => item?.wasPressed).length
            }
        }), 0);
        //console.log(keyCount)
        return keyCount?.split(',').reduce(function (p, c) {return p + +c}, 0)
    }//count how many keys were pressed to calculate percentage
    const percentageCalc = () => setPercentage(setDynamicPercentage(keyAmount(true), keyAmount()));//Percentage calculation
    const setDynamicPercentage = (keysAlreadyPressed, keys) => {
        let percentage = Math.floor(keysAlreadyPressed / keys * 100)
        //console.log(" [ " + percentage + " ] ", " [ " + (keysAlreadyPressed / keys * 100) + " ] ",  " [ " + (keysAlreadyPressed) + " ] ",  " [ " + (keys) + " ] ")
        if (percentage >= 100) {
            return 100;
        }
        return percentage;
    }
    const handleKeyboardFail = async () => {
        if (keyAmount(true) >= keyAmount()) return;
        setPercentage(100)
        completionModalShownRef.current = false;
        updateKeyboard({
            keyboard: {
                ...keyboard,
                types: keyboard.types.map((item, i) => {
                    if (item.active) {
                        return {
                            ...item,
                            results: activeKeyboardType?.keys.map((sections) => {
                                return {
                                    ...sections,
                                    section: sections.section.map((section) => {
                                        return {
                                            ...section,
                                            row: section.row.map((row) => {
                                                if (item?.finalResult === "passed") {
                                                    return {
                                                        ...row,
                                                        failed: true,
                                                        wasPressed: false
                                                    }
                                                }
                                                if (!row?.wasPressed && !row?.excluded) {
                                                    return {
                                                        ...row,
                                                        failed: true
                                                    }
                                                }
                                                return {
                                                    ...row
                                                }
                                            })
                                        }
                                    })
                                }
                            }),
                            keys: activeKeyboardType?.keys.map((sections) => {
                                return {
                                    ...sections,
                                    section: sections.section.map((section) => {
                                        return {
                                            ...section,
                                            row: section.row.map((row) => {
                                                if (item?.finalResult === "passed") {
                                                    return {
                                                        ...row,
                                                        failed: true,
                                                        wasPressed: false
                                                    }
                                                }
                                                if (!row?.wasPressed && !row?.excluded) {
                                                    return {
                                                        ...row,
                                                        failed: true
                                                    }
                                                }
                                                return {
                                                    ...row
                                                }
                                            })
                                        }
                                    })
                                }
                            })
                        }
                    }
                    return {
                        ...item
                    }
                })
            }
        })

    }//Fail keyboard
    const failButtonWithTooltip = () => {
        if (keyAmount(true) >= keyAmount()) {
            return <OverlayTrigger
                placement="bottom"
                overlay={
                    <Tooltip id="tooltip-top">
                        <div>
                            As long as all keys are successfully captured, the test <b>cannot be failed</b>.
                        </div>
                    </Tooltip>
                }
            >
                <span className={showMode ? "keyboard-show-mode" : ""}>
                    <button className="btn btn-danger disabled" type="button" onClick={handleKeyboardFail}>
                        <FaTimes />
                        Fail
                    </button>
                </span>
            </OverlayTrigger>
        }
        return <button className={`btn btn-danger ${showMode ? "keyboard-show-mode" : ""}`} type="button" onClick={handleKeyboardFail}>
            <FaTimes />
            Fail
        </button>
    }
    const handleKeyboardPass = () => {
        setPercentage(100)
        completionModalShownRef.current = false;
        updateKeyboard({
            keyboard: {
                ...keyboard,
                types: keyboard.types.map((item, i) => {
                    if (item.active) {
                        return {
                            ...item,
                            results: activeKeyboardType?.keys.map((sections) => {
                                return {
                                    ...sections,
                                    section: sections.section.map((section) => {
                                        return {
                                            ...section,
                                            row: section.row.map((row) => {
                                                return {
                                                    ...row,
                                                    wasPressed: true,
                                                    failed: false
                                                }
                                            })
                                        }
                                    })
                                }
                            }),
                            keys: activeKeyboardType?.keys.map((sections) => {
                                return {
                                    ...sections,
                                    section: sections.section.map((section) => {
                                        return {
                                            ...section,
                                            row: section.row.map((row) => {
                                                if (!row?.excluded) {
                                                    return {
                                                        ...row,
                                                        wasPressed: true,
                                                        failed: false
                                                    }
                                                }
                                                return {
                                                    ...row
                                                }
                                            })
                                        }
                                    })
                                }
                            })
                        }
                    }
                    return {
                        ...item
                    }
                })
            }
        })
    }//Pass keyboard
    const resetKeyboard = () => {
        setPercentage(0)
        completionModalShownRef.current = false;
        updateKeyboard({
            keyboard: {
                ...keyboard,
                types: keyboard.types.map((item, i) => {
                    if (item.active) {
                        return {
                            ...item,
                            results: [],
                            keys: activeKeyboardType?.keys.map((sections) => {
                                return {
                                    ...sections,
                                    section: sections.section.map((section) => {
                                        return {
                                            ...section,
                                            row: section.row.map((row) => {
                                                return {
                                                    ...row,
                                                    wasPressed: false,
                                                    failed: false
                                                }
                                            })
                                        }
                                    })
                                }
                            })
                        }
                    }
                    return {
                        ...item
                    }
                })
            }
        })
    }//Reset keyboard
    const findFailedKeys = (obj, keyToFind) => {
        if (!isWalkableValue(obj)) return [];
        return Object.entries(obj).reduce((acc, [key, value]) => {
            if (key === keyToFind) {
                return acc.concat(value);
            }
            if (isWalkableValue(value)) {
                return acc.concat(findFailedKeys(value, keyToFind));
            }
            return acc;
        }, []);
    } //find failed keys
    const test = () => {
        percentageCalc();
        //setPercentage(100)
        updateKeyboard({
            keyboard: {
                ...keyboard,
                types: keyboard.types.map((item, i) => {
                    if (item.active) {
                        return {
                            ...item,
                            results: activeKeyboardType?.keys.map((sections) => {
                                return {
                                    ...sections,
                                    section: sections.section.map((section) => {
                                        return {
                                            ...section,
                                            row: section.row.map((row) => {
                                                return {
                                                    ...row,
                                                    wasPressed: true,
                                                    failed: false
                                                }
                                            })
                                        }
                                    })
                                }
                            }),
                            keys: activeKeyboardType?.keys.map((sections) => {
                                return {
                                    ...sections,
                                    section: sections.section.map((section) => {
                                        return {
                                            ...section,
                                            row: section.row.map((row) => {
                                                if (row?.name === "Escape" || row?.name === "F1" || row?.name === "F2" || row?.name === "F3") {
                                                    return {
                                                        ...row
                                                    }
                                                }
                                                if (!row?.excluded) {
                                                    return {
                                                        ...row,
                                                        wasPressed: true,
                                                        failed: false
                                                    }
                                                }
                                                return {
                                                    ...row
                                                }
                                            })
                                        }
                                    })
                                }
                            })
                        }
                    }
                    return {
                        ...item
                    }
                })
            }
        })
    }//Pass more than half keyboard keys for testing purposes

    const downHandler = useCallback((event) => {
        if (!keyboard?.types) return;
        //console.log("EVENT", event)
        event.preventDefault();
        setJustPressedKey(event?.code);
        updateKeyboard({
            keyboard: {
                ...keyboard,
                types: keyboard.types.map((item, i) => {
                    if (item?.active) {
                        return {
                            ...item,
                            keys: activeKeyboardType?.keys.map((sections) => {
                                return {
                                    ...sections,
                                    section: sections?.section?.map((section) => {
                                        return {
                                            ...section,
                                            row: section?.row?.map((row) => {
                                                if (row?.name === event?.code && specialKeysWerePressed()) { //We cannot allow typing any other key if special keys were not pushed.
                                                    return {
                                                        ...row,
                                                        wasPressed: true,
                                                        isPressed: row?.name === event?.key || row?.name === event?.code
                                                    }
                                                }
                                                return {
                                                    ...row,
                                                    isPressed: false
                                                }
                                            })
                                        }
                                    })
                                }
                            })
                        }
                    }
                    return {
                        ...item
                    }
                })
            }
        })
    }, [activeKeyboardType?.keys]);//Handle key press
    const upHandler = useCallback(() => {
        if (!keyboard?.types) return;
        percentageCalc();
        updateKeyboard({
            keyboard: {
                ...keyboard,
                types: keyboard.types.map((item, i) => {
                    if (item.active) {
                        return {
                            ...item,
                            keys: activeKeyboardType?.keys.map((sections) => {
                                return {
                                    ...sections,
                                    section: sections.section.map((section) => {
                                        return {
                                            ...section,
                                            row: section.row.map((row) => {
                                                return {
                                                    ...row,
                                                    isPressed: false
                                                }
                                            })
                                        }
                                    })
                                }
                            })
                        }
                    }
                    return {
                        ...item
                    }
                })
            }
        })

    }, [activeKeyboardType?.keys]);//Handle key release

    const [justPresedKey, setJustPressedKey] = useState("");
    useEffect(() => {
        if (specialKeysWerePressed()) return;
        if (justPresedKey === "") return;
        messageForSpecialKeys();

    }, [justPresedKey]);

    useEffect(() => {
        if (!keyboard?.types?.length || !activeKeyboardType || !activeHasBuiltInFnKey) return;
        if (!activeHasSwitchableFnKey && activeKeyboardType?.bottomRowVariant !== "fn") return;

        updateKeyboard({
            keyboard: {
                ...keyboard,
                types: keyboard.types.map((item) => {
                    if (!item.active) return item;
                    return {
                        ...item,
                        bottomRowVariant: "menu",
                        keys: mapSwitchableBottomRows(item.keys, "menu"),
                        results: mapSwitchableBottomRows(item.results, "menu"),
                    };
                })
            }
        });
    }, [activeKeyboardType?.name, activeKeyboardType?.bottomRowVariant, activeHasBuiltInFnKey, activeHasSwitchableFnKey]);

    useEffect(() => {
        const expectedKeyboardTypes = [
            "fullsize",
            "eighteenhundred",
            "ninetysix",
            "tenkeyless",
            "seventyfive-exploded",
            "seventyfive",
            "sixtyfive-exploded",
            "sixtyfive",
            "compact",
            "fifty",
            "forty"
        ];
        const hasAllKeyboardTypes = expectedKeyboardTypes.every((name) => keyboard?.types?.some((type) => type?.name === name));
        const onlyExpectedKeyboardTypes = keyboard?.types?.every((type) => expectedKeyboardTypes.includes(type?.name));
        const fullSizeKeyNames = getPhysicalKeyboardRows(identifKeyboard("fullsize")?.keys).map((row) => row.name);
        const fullSizeIsCurrent = fullSizeKeyNames.includes("MetaRight") && fullSizeKeyNames.length === FULL_SIZE_KEY_ORDER.length;
        const tenKeylessKeyNames = getPhysicalKeyboardRows(identifKeyboard("tenkeyless")?.keys).map((row) => row.name);
        const tenKeylessIsCurrent = tenKeylessKeyNames.includes("MetaRight") && tenKeylessKeyNames.length === TENKEYLESS_KEY_ORDER.length;
        const seventyFiveExplodedKeyNames = getPhysicalKeyboardRows(identifKeyboard("seventyfive-exploded")?.keys).map((row) => row.name);
        const seventyFiveExplodedIsCurrent = seventyFiveExplodedKeyNames.length === SEVENTY_FIVE_EXPLODED_KEY_ORDER.length &&
            SEVENTY_FIVE_EXPLODED_KEY_ORDER.every((keyName) => seventyFiveExplodedKeyNames.includes(keyName)) &&
            !["Home", "MetaRight"].some((keyName) => seventyFiveExplodedKeyNames.includes(keyName));
        const seventyFiveKeyNames = getPhysicalKeyboardRows(identifKeyboard("seventyfive")?.keys).map((row) => row.name);
        const seventyFiveIsCurrent = seventyFiveKeyNames.length === SEVENTY_FIVE_COMPACT_KEY_ORDER.length &&
            SEVENTY_FIVE_COMPACT_KEY_ORDER.every((keyName) => seventyFiveKeyNames.includes(keyName)) &&
            seventyFiveKeyNames.includes("MetaRight");
        const sixtyFiveExplodedKeyNames = getPhysicalKeyboardRows(identifKeyboard("sixtyfive-exploded")?.keys).map((row) => row.name);
        const sixtyFiveExplodedIsCurrent = sixtyFiveExplodedKeyNames.length === SIXTY_FIVE_EXPLODED_KEY_ORDER.length &&
            SIXTY_FIVE_EXPLODED_KEY_ORDER.every((keyName) => sixtyFiveExplodedKeyNames.includes(keyName)) &&
            sixtyFiveExplodedKeyNames.includes("Backquote");
        const sixtyFiveKeyNames = getPhysicalKeyboardRows(identifKeyboard("sixtyfive")?.keys).map((row) => row.name);
        const sixtyFiveIsCurrent = sixtyFiveKeyNames.length === SIXTY_FIVE_COMPACT_KEY_ORDER.length &&
            SIXTY_FIVE_COMPACT_KEY_ORDER.every((keyName) => sixtyFiveKeyNames.includes(keyName)) &&
            !sixtyFiveKeyNames.includes("Backquote");
        const compactKeyNames = getPhysicalKeyboardRows(identifKeyboard("compact")?.keys).map((row) => row.name);
        const compactIsCurrent = compactKeyNames.length === COMPACT_KEY_ORDER.length &&
            COMPACT_KEY_ORDER.every((keyName) => compactKeyNames.includes(keyName)) &&
            !compactKeyNames.includes("Backquote");
        const fiftyKeyNames = getPhysicalKeyboardRows(identifKeyboard("fifty")?.keys).map((row) => row.name);
        const fiftyIsCurrent = fiftyKeyNames.length === FIFTY_KEY_ORDER.length &&
            FIFTY_KEY_ORDER.every((keyName) => fiftyKeyNames.includes(keyName)) &&
            !["Digit1", "Digit2", "Digit3"].some((keyName) => fiftyKeyNames.includes(keyName));
        const fortyKeyNames = getPhysicalKeyboardRows(identifKeyboard("forty")?.keys).map((row) => row.name);
        const fortyIsCurrent = fortyKeyNames.length === FORTY_KEY_ORDER.length &&
            FORTY_KEY_ORDER.every((keyName) => fortyKeyNames.includes(keyName)) &&
            !["Digit1", "Digit2", "Digit3", "BracketLeft"].some((keyName) => fortyKeyNames.includes(keyName));
        const ninetySixKeyNames = getPhysicalKeyboardRows(identifKeyboard("ninetysix")?.keys).map((row) => row.name);
        const ninetySixIsCurrent = ninetySixKeyNames.length === NINETY_SIX_KEY_ORDER.length &&
            NINETY_SIX_KEY_ORDER.every((keyName) => ninetySixKeyNames.includes(keyName)) &&
            !["ScrollLock", "Pause", "Insert"].some((keyName) => ninetySixKeyNames.includes(keyName));
        const eighteenHundredKeyNames = getPhysicalKeyboardRows(identifKeyboard("eighteenhundred")?.keys).map((row) => row.name);
        const eighteenHundredIsCurrent = EIGHTEEN_HUNDRED_KEY_ORDER.every((keyName) => eighteenHundredKeyNames.includes(keyName)) &&
            !["Insert", "Home", "PageUp", "PageDown", "PrintScreen", "ScrollLock", "Pause", "Delete", "End"].some((keyName) => eighteenHundredKeyNames.includes(keyName));
        if (keyboard?.types?.length && hasAllKeyboardTypes && onlyExpectedKeyboardTypes && fullSizeIsCurrent && tenKeylessIsCurrent && seventyFiveExplodedIsCurrent && seventyFiveIsCurrent && sixtyFiveExplodedIsCurrent && sixtyFiveIsCurrent && compactIsCurrent && fiftyIsCurrent && fortyIsCurrent && ninetySixIsCurrent && eighteenHundredIsCurrent) return;

        const fullsize = identifKeyboard("fullsize");
        const eighteenhundred = identifKeyboard("eighteenhundred");
        const ninetysix = identifKeyboard("ninetysix");
        const tenkeyless = identifKeyboard("tenkeyless");
        const seventyfiveExploded = identifKeyboard("seventyfive-exploded");
        const seventyfive = identifKeyboard("seventyfive");
        const sixtyfiveExploded = identifKeyboard("sixtyfive-exploded");
        const sixtyfive = identifKeyboard("sixtyfive");
        const compact = identifKeyboard("compact");
        const fifty = identifKeyboard("fifty");
        const forty = identifKeyboard("forty");
        const existingActiveTypeName = keyboard?.types?.find((type) => type?.active)?.name || "fullsize";
        const migratedActiveTypeName = {
            "compact-fullsize": "fullsize",
            "seventyfive-old": "seventyfive",
        }[existingActiveTypeName] || existingActiveTypeName;
        const activeTypeName = expectedKeyboardTypes.includes(migratedActiveTypeName) ? migratedActiveTypeName : "fullsize";

        updateKeyboard({
            keyboard: {
                types: [
                    {
                        name: "fullsize",
                        results: fullsize && fullSizeIsCurrent ? [...fullsize?.results] : [],
                        keys: fullsize && fullSizeIsCurrent ? [...fullsize?.keys] : fullSizeKeys,
                        layout: "fullsize",
                        active: activeTypeName === "fullsize"
                    },
                    {
                        name: "eighteenhundred",
                        results: eighteenhundred ? [...eighteenhundred?.results] : [],
                        keys: eighteenhundred && eighteenHundredIsCurrent ? [...eighteenhundred?.keys] : eighteenHundredKeys,
                        layout: "eighteenhundred",
                        active: activeTypeName === "eighteenhundred"
                    },
                    {
                        name: "ninetysix",
                        results: ninetysix && ninetySixIsCurrent ? [...ninetysix?.results] : [],
                        keys: ninetysix && ninetySixIsCurrent ? [...ninetysix?.keys] : ninetySixKeys,
                        layout: "ninetysix",
                        active: activeTypeName === "ninetysix"
                    },
                    {
                        name: "tenkeyless",
                        results: tenkeyless && tenKeylessIsCurrent ? [...tenkeyless?.results] : [],
                        keys: tenkeyless && tenKeylessIsCurrent ? [...tenkeyless?.keys] : tenKeylessKeys,
                        layout: "tenkeyless",
                        active: activeTypeName === "tenkeyless"
                    },
                    {
                        name: "seventyfive-exploded",
                        results: seventyfiveExploded && seventyFiveExplodedIsCurrent ? [...seventyfiveExploded?.results] : [],
                        keys: seventyfiveExploded && seventyFiveExplodedIsCurrent ? [...seventyfiveExploded?.keys] : seventyFiveExplodedKeys,
                        layout: "seventyfive-exploded",
                        active: activeTypeName === "seventyfive-exploded"
                    },
                    {
                        name: "seventyfive",
                        results: seventyfive && seventyFiveIsCurrent ? [...seventyfive?.results] : [],
                        keys: seventyfive && seventyFiveIsCurrent ? [...seventyfive?.keys] : seventyFiveKeys,
                        layout: "seventyfive",
                        active: activeTypeName === "seventyfive"
                    },
                    {
                        name: "sixtyfive-exploded",
                        results: sixtyfiveExploded && sixtyFiveExplodedIsCurrent ? [...sixtyfiveExploded?.results] : [],
                        keys: sixtyfiveExploded && sixtyFiveExplodedIsCurrent ? [...sixtyfiveExploded?.keys] : sixtyFiveExplodedKeys,
                        layout: "sixtyfive-exploded",
                        active: activeTypeName === "sixtyfive-exploded"
                    },
                    {
                        name: "sixtyfive",
                        results: sixtyfive && sixtyFiveIsCurrent ? [...sixtyfive?.results] : [],
                        keys: sixtyfive && sixtyFiveIsCurrent ? [...sixtyfive?.keys] : sixtyFiveKeys,
                        layout: "sixtyfive",
                        active: activeTypeName === "sixtyfive"
                    },
                    {
                        name: "compact",
                        results: compact && compactIsCurrent ? [...compact?.results] : [],
                        keys: compact && compactIsCurrent ? [...compact?.keys] : compactLayoutKeys,
                        layout: "compact",
                        active: activeTypeName === "compact"
                    },
                    {
                        name: "fifty",
                        results: fifty && fiftyIsCurrent ? [...fifty?.results] : [],
                        keys: fifty && fiftyIsCurrent ? [...fifty?.keys] : fiftyKeys,
                        layout: "fifty",
                        active: activeTypeName === "fifty"
                    },
                    {
                        name: "forty",
                        results: forty && fortyIsCurrent ? [...forty?.results] : [],
                        keys: forty && fortyIsCurrent ? [...forty?.keys] : fortyKeys,
                        layout: "forty",
                        active: activeTypeName === "forty"
                    }
                ]
            }
        })
    }, [keyboard?.types?.length]);// Prepare arrays
    useEffect(() => {
        if (!showMode) {
            window.addEventListener("keydown", downHandler);
            window.addEventListener("keyup", upHandler);
            return () => {
                window.removeEventListener("keydown", downHandler);
                window.removeEventListener("keyup", upHandler);
            };
        }
    }, [downHandler]);//Press and release events
    useEffect(() => {
        const interval = setInterval(checkStatus, 1000);
        return () => clearInterval(interval);
    }, [activeKeyboardType]) //check status of special keys on activeKeyboardType change
    useEffect(() => {
        if (parseInt(percentage.toFixed(0)) >= 100) {
            if (completionModalShownRef.current) return;
            completionModalShownRef.current = true;
            message()
        }
    }, [percentage, activeKeyboardType?.results]);//Display message on success
    useEffect(() => {
        if (typeof activeKeyboardType?.results === "undefined") return;
        if (activeKeyboardType?.results?.length === 0) return;

        updateKeyboard({
            keyboard: {
                ...keyboard,
                types: keyboard.types.map((item, i) => {
                    if (item.active) {
                        return {
                            ...item,
                            finalResult: findFailedKeys(activeKeyboardType?.results, 'failed')?.some(f => f) ? "failed" : "passed"
                        }
                    }
                    return {
                        ...item
                    }
                })
            }
        })
    }, [activeKeyboardType?.results]); //Put apropriate result status into active keyboard

    if (!keyboard?.types?.length || !activeKeyboardType) {
        return (
            <div className="keyboard">
                <div className="alert alert-light border mb-0">Preparing keyboard layout…</div>
            </div>
        );
    }

    const sortedSilhouetteKeys = activeSilhouetteLayout
        ? activeKeyboardLayout === "fullsize"
            ? sortSilhouetteKeys(activeSilhouetteLayout.keys)
            : activeKeyboardLayout === "eighteenhundred"
                ? getEighteenHundredSilhouetteKeys(activeSilhouetteLayout.keys)
                : sortSilhouetteKeys(activeSilhouetteLayout.keys)
        : [];
    const silhouetteKeyStyleMap = activeSilhouetteLayout
        ? activeKeyboardLayout === "fullsize"
            ? mapStylesByKeyOrder(FULL_SIZE_KEY_ORDER, sortedSilhouetteKeys)
            : activeKeyboardLayout === "eighteenhundred"
                ? mapStylesByKeyOrder(EIGHTEEN_HUNDRED_KEY_ORDER, sortedSilhouetteKeys)
                : activeKeyboardLayout === "ninetysix"
                    ? mapStylesByKeyOrder(NINETY_SIX_KEY_ORDER, sortedSilhouetteKeys)
                    : activeKeyboardLayout === "tenkeyless"
                        ? mapStylesByKeyOrder(TENKEYLESS_KEY_ORDER, sortedSilhouetteKeys)
                        : activeKeyboardLayout === "seventyfive-exploded"
                            ? mapStylesByKeyOrder(SEVENTY_FIVE_EXPLODED_KEY_ORDER, sortedSilhouetteKeys)
                            : activeKeyboardLayout === "seventyfive"
                                ? mapStylesByKeyOrder(SEVENTY_FIVE_COMPACT_KEY_ORDER, sortedSilhouetteKeys)
                                : activeKeyboardLayout === "sixtyfive-exploded"
                                    ? mapStylesByKeyOrder(SIXTY_FIVE_EXPLODED_KEY_ORDER, sortedSilhouetteKeys)
                                    : activeKeyboardLayout === "sixtyfive"
                                        ? mapStylesByKeyOrder(SIXTY_FIVE_COMPACT_KEY_ORDER, sortedSilhouetteKeys)
                                        : activeKeyboardLayout === "compact"
                                            ? mapStylesByKeyOrder(COMPACT_KEY_ORDER, sortedSilhouetteKeys)
                                            : activeKeyboardLayout === "fifty"
                                                ? mapStylesByKeyOrder(FIFTY_KEY_ORDER, sortedSilhouetteKeys)
                                                : activeKeyboardLayout === "forty"
                                                    ? mapStylesByKeyOrder(FORTY_KEY_ORDER, sortedSilhouetteKeys)
                                                    : getPhysicalKeyboardRows(activeKeyboardType?.keys).reduce((styleMap, row, index) => {
            const silhouetteKey = sortedSilhouetteKeys[index];
            if (silhouetteKey) styleMap[row.name] = silhouetteKey;
            return styleMap;
        }, {})
        : {};
    const getSilhouetteKeyStyle = (row) => {
        if (!activeSilhouetteLayout) return undefined;
        return silhouetteKeyStyleMap[row?.name] || {display: "none"};
    };
    const keyboardAreaStyle = activeSilhouetteLayout
        ? {
            aspectRatio: activeSilhouetteLayout.ratio,
            width: "100%",
            "--plate-x": activeSilhouetteLayout.plateX,
            "--plate-y": activeSilhouetteLayout.plateY,
        }
        : undefined;

    return (
        <div className="keyboard">
            <div className="tools-test-heading">
                <Link href="/tools" aria-label="Back to Tools"><FaChevronLeft /></Link>
                <FaKeyboard size={36} />
                <div>
                    <h1>Keyboard Test</h1>
                    <p>Press each key once to verify your keyboard. Choose your layout below.</p>
                </div>
            </div>
            {/*<button onClick={test}>Select most buttons for testing purposes</button>*/}
            <hr className="mt-0" />
            <div className="alert alert-light border">
                <FaInfoCircle className="mr-2 text-info" /> Browser note: Windows/Meta and Fn keys cannot be captured in a web browser. They are shown on the layout for reference only and are excluded from the required key count.
            </div>
            {percentage !== 100 ?
                // <b className="special-key-color">PrtSc</b>,
                <div className="alert alert-light border">
                    <FaQuestionCircle className="mr-2 text-info" /> Select the closest keyboard type, then type each available key <b>one by one</b>.
                    <br />
                    <FaExclamationTriangle className="mr-2 text-warning" /> Test results may not be accurate if you press multiple keys at once or type too quickly.
                </div>
                :
                findFailedKeys(activeKeyboardType?.results, 'failed')?.some(f => f)
                    ?
                    <div className={`alert alert-danger m-0 mb-3`}>
                        <FaTimes className="mr-2" /> Some problem(s) reported by user...
                    </div>
                    :
                    <div className={`alert alert-success m-0 mb-3`}>
                        <FaCheck className="mr-2" /> All keys were captured. Test was successful
                    </div>
            }

            <KeyboardType />

            <div className={`keyboard-action-panel ${showMode ? "keyboard-show-mode" : ""}`}>
                <div className="keyboard-action-group">
                    <div className="keyboard-action-label">Bottom row</div>
                    <div className="keyboard-actions">
                        <button
                            type="button"
                            className={`btn btn-outline-secondary keyboard-variant-button ${activeKeyboardType?.bottomRowVariant !== "fn" ? "active" : ""}`}
                            onClick={() => setBottomRowVariant("menu")}
                        >
                            <CgMenuBoxed />
                            Menu key
                        </button>
                        <button
                            type="button"
                            className={`btn btn-outline-secondary keyboard-variant-button ${!activeHasBuiltInFnKey && activeKeyboardType?.bottomRowVariant === "fn" ? "active" : ""}`}
                            disabled={activeHasBuiltInFnKey}
                            onClick={() => setBottomRowVariant("fn")}
                            title={activeHasBuiltInFnKey ? "This layout already has an Fn key" : "Fn cannot be captured by the browser; it is shown for layout matching only"}
                        >
                            Fn key
                        </button>
                    </div>
                </div>

                <div className="keyboard-action-group">
                    <div className="keyboard-action-label">Result</div>
                    <div className="keyboard-actions">
                        <button className="btn btn-success" type="button" onClick={handleKeyboardPass}>
                            <FaCheck />
                            Pass
                        </button>
                        {failButtonWithTooltip()}
                    </div>
                </div>

                <div className="keyboard-action-group">
                    <div className="keyboard-action-label">Maintenance</div>
                    <div className="keyboard-actions">
                        <button className="btn btn-secondary" type="button" onClick={resetKeyboard}>
                            <FaRedo />
                            Reset keyboard
                        </button>
                    </div>
                </div>
            </div>

            <div className={`keyboard-shell ${showMode ? "border-top" : ""}`}>
                <div className={`keyboard-progress ${showMode ? "keyboard-show-mode" : ""}`}>
                    <div className="progress" style={{height: "1px"}}>
                        <div className="progress-bar" role="progressbar" style={{width: percentage + "%"}} aria-valuenow={percentage} aria-valuemin="0" aria-valuemax="100" />
                    </div>
                </div>

                <div className={`keyboard-percentage text-center ${showMode ? "keyboard-show-mode" : ""}`}>Progress {percentage.toFixed(0)}%</div>
                <div
                    className={`keyboard-keys-area ${activeKeyboardLayout} ${activeSilhouetteLayout ? "keyboard-silhouette-layout" : ""}`}
                    style={keyboardAreaStyle}
                >
                    {activeKeyboardType && activeKeyboardType?.keys?.map((sections, secsIndx) =>
                        <div className="keyboard-section" key={secsIndx}>
                            {sections?.section?.map((section, secIndx) =>
                                <div key={secIndx} className="keyboard-row">
                                    {section?.row?.map((row, rowIndx) =>
                                        <div key={rowIndx} className={`keyboard-key ${row?.class ?? ""}  
                                        
                                        ${row?.name} ${row?.isPressed ? "is-pressed" : ""} 
                                        
                                        ${row?.wasPressed ? "was-pressed" : ""} 
                                        
                                        ${row?.failed ? "failed-key" : ""}
                                        
                                        `}
                                            style={getSilhouetteKeyStyle(row)}
                                        >
                                            <FaTimes className="key-failed-X" /> {row?.keys?.map((altKey, akIndex) =>
                                            <div key={akIndex} className={`key-alternatives${row?.keys?.length === 2 ? "-dual" : ""}`}>
                                                {altKey}
                                            </div>
                                        )}

                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

export default KeyboardTest;
